import { useEffect, useState, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { tagsListSelector, getTaskTagsAction } from 'store/taskTags'
import Icon from 'ui/components/Icon'
import { notifyError } from 'ui/components/Notify'
import { MAX_TAGS_PER_TASK } from 'utils/constants/taskTags'
import {
  InputWrap,
  InputLabel,
  TagsContainer,
  PlaceholderStyled,
  TagStyled,
  TagButton,
  InputContainer,
  Input,
  SuggestionWrap,
  HiddenSpan,
  MatchingTagsContainer,
  MatchingTag,
  ArrowIcon,
} from './styled'

const InputTags = ({
  label,
  tags,
  setTags,
  tagIdsToUpdate,
  tagIdsToDelete,
}) => {
  const dispatch = useDispatch()
  const taskTags = useSelector(tagsListSelector)
  const [inputValue, setInputValue] = useState('')
  const [suggestion, setSuggestion] = useState('')
  const [isFocused, setIsFocused] = useState(false)
  const [matchingTags, setMatchingTags] = useState([])
  const [openMatchingTags, setOpenMatchingTags] = useState(false)

  const spanRef = useRef(null)
  const inputRef = useRef(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    dispatch(getTaskTagsAction())
  }, [dispatch])

  useEffect(() => {
    if (spanRef.current && inputRef.current) {
      const spanWidth = spanRef.current.offsetWidth
      inputRef.current.style.width = `${spanWidth}px`
    }
  }, [inputValue])

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target)) {
        setOpenMatchingTags(false)
        setIsFocused(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  useEffect(() => {
    if (!openMatchingTags) {
      setMatchingTags([])
      return
    }

    const value = (inputValue ?? '').toLowerCase()
    const selectedIds = new Set(
      (Array.isArray(tags) ? tags : []).map((tag) => tag.id)
    )

    const next = (Array.isArray(taskTags) ? taskTags : []).filter(
      (tag) =>
        tag?.is_active === true &&
        !selectedIds.has(tag.id) &&
        (value ? tag.name.toLowerCase().includes(value) : true)
    )

    setMatchingTags(next)
  }, [tags, taskTags, inputValue, openMatchingTags])

  const findSuggestion = (value) => {
    if (!value) return ''
    const availableTags = (Array.isArray(taskTags) ? taskTags : []).filter(
      (tag) => !tags.find((t) => t.id === tag.id)
    )
    const match = availableTags.find(
      (tag) =>
        tag?.is_active === true &&
        tag.name.toLowerCase().startsWith(value.toLowerCase())
    )
    if (match && match?.name.toLowerCase() !== value.toLowerCase()) {
      return match.name.slice(value.length)
    }
    return ''
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    setSuggestion(findSuggestion(value))
    setOpenMatchingTags(true)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Tab') {
      setOpenMatchingTags(false)
      return
    }

    if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      const lastTag = tags[tags.length - 1]
      setTags(tags.slice(0, -1))
      tagIdsToUpdate((prev) => prev.filter((id) => id !== lastTag.id))
      tagIdsToDelete((prev) =>
        prev.includes(lastTag.id) ? prev : [...prev, lastTag.id]
      )
    }
  }

  const handleAddTag = (tag) => {
    if (tags.length === MAX_TAGS_PER_TASK) {
      notifyError(`Максимум ${MAX_TAGS_PER_TASK} тегов для одной задачи`)
    } else {
      setTags([...tags, tag])
      tagIdsToDelete((prev) => prev.filter((id) => id !== tag.id))
      tagIdsToUpdate((prev) =>
        prev.includes(tag.id) ? prev : [...prev, tag.id]
      )
    }
    setInputValue('')
    setSuggestion('')
  }

  const handleRemoveTag = (e) => {
    e.preventDefault()
    const button = e.target.closest('button[data-id]')
    const arrowButton = e.target.closest('div[data-arrow]')

    if (button) {
      const tagId = parseInt(button.dataset.id, 10)
      setTags(tags.filter((tag) => tag.id !== tagId))
      tagIdsToUpdate((prev) => prev.filter((id) => id !== tagId))
      tagIdsToDelete((prev) => (prev.includes(tagId) ? prev : [...prev, tagId]))
    } else if (arrowButton) {
      setOpenMatchingTags((prev) => !prev)
    } else {
      inputRef.current?.focus()
    }
  }

  const handleFocus = () => {
    setOpenMatchingTags(true)
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  return (
    <InputWrap ref={wrapRef} onClick={(e) => handleRemoveTag(e)}>
      <InputLabel>{label}</InputLabel>
      <TagsContainer>
        {tags.length === 0 && !inputValue && !isFocused && (
          <PlaceholderStyled>{label}</PlaceholderStyled>
        )}

        {tags.map((tag) => (
          <TagStyled key={tag.id} color={tag.color} bgColor={tag.bg_color}>
            {tag.name}
            <TagButton type="button" color={tag.color} data-id={tag.id}>
              <Icon size="10" name="close" />
            </TagButton>
          </TagStyled>
        ))}

        <InputContainer>
          <Input
            ref={inputRef}
            type="text"
            name="taskTags"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete="off"
          />
          <HiddenSpan ref={spanRef}>{inputValue || ' '}</HiddenSpan>
          {suggestion && <SuggestionWrap>{suggestion}</SuggestionWrap>}
        </InputContainer>

        <ArrowIcon data-arrow="arrow">
          <Icon name="arrow-down" size={'20'} />
        </ArrowIcon>
      </TagsContainer>
      {openMatchingTags && (
        <MatchingTagsContainer>
          {matchingTags.length > 0
            ? matchingTags.map((tag) => (
                <MatchingTag
                  key={tag.id}
                  onClick={() => {
                    handleAddTag(tag)
                  }}
                >
                  <TagStyled color={tag.color} bgColor={tag.bg_color}>
                    {tag.name}
                  </TagStyled>
                </MatchingTag>
              ))
            : 'Ничего не найдено'}
        </MatchingTagsContainer>
      )}
    </InputWrap>
  )
}

export default InputTags
